/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core';
import {
    array, bool, func, shape, string,
} from 'prop-types';
import { useSelector } from 'react-redux';
import { getFirstLastName } from '../../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPresentationFamily } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { useAuth } from '../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';

const useStyles = makeStyles((theme) => ({
    header: ({ presentationFamily }) => ({
        width: '335px',
        background: presentationFamily === 'flower' ? (theme.palette.cms?.mobileBgHeader || theme.palette?.bgHeader) : theme.palette.cms?.white,
        minHeight: '50px',
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
        display: 'flex',
        alignContent: 'center',
        alignItems: 'center',
        padding: '0 10px',
        justifyContent: 'space-between',
    }),
    headerContainer: {
        minHeight: '50px',
        position: 'relative',
        zIndex: 9,
        background: '#fff',
    },
    headerLogo: ({ presentationFamily }) => ({
        display: 'flex',
        '& img': {
            height: presentationFamily === 'flower' ? 48 : 28,
        },
    }),
    closeSection: {
        display: 'flex',
        alignItems: 'center',
    },
    signInContainer: ({ presentationFamily }) => ({
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '10px',
        marginTop: '10px',
        textTransform: 'capitalize',
        fontFamily: 'var(--comp-base-font-family-default, Lato , Helvetica, Arial, sans-serif)',
        '& span': {
            fontSize: '9px',
            color: presentationFamily === 'flower' ? (theme.palette.cms?.mobileMenuTextColor || theme.palette?.mobileMenuColor) : '',
        },
        '& img': {
            width: '17px',
        },
    }),
    closeIcon: {
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        '& img': {
            width: '14px',
        },
        '& svg > path': {
            fill: theme.palette.cms?.mobileMenuTextColor || theme.palette?.mobileMenuColor,
        },
    },
    signInLink: {
        display: 'contents',
        '& svg > path': {
            fill: theme.palette.cms?.mobileMenuTextColor || theme.palette?.mobileMenuColor,
        },
    },
}));

const MenuHeader = ({
    menu, handleClickMenu, logo, isAuthenticated,
}) => {
    const presentationFamily = useSelector(getPresentationFamily);
    const classes = useStyles({ presentationFamily });
    const getFullName = useSelector(getFirstLastName);
    const { loginByRedirect } = useAuth();

    const headerMenuIcon = {
        common: {
            signInIcon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M14 4.375C11.9059 4.375 10.2084 6.07259 10.2084 8.16667C10.2084 10.2607 11.9059 11.9583 14 11.9583C16.0941 11.9583 17.7917 10.2607 17.7917 8.16667C17.7917 6.07259 16.0941 4.375 14 4.375ZM8.45836 8.16667C8.45836 5.10609 10.9394 2.625 14 2.625C17.0606 2.625 19.5417 5.10609 19.5417 8.16667C19.5417 11.2272 17.0606 13.7083 14 13.7083C10.9394 13.7083 8.45836 11.2272 8.45836 8.16667ZM5.41477 18.2481C6.45404 17.2089 7.86358 16.625 9.33332 16.625H18.6667C20.1364 16.625 21.5459 17.2089 22.5852 18.2481C23.6245 19.2874 24.2083 20.6969 24.2083 22.1667V24.5C24.2083 24.9832 23.8166 25.375 23.3333 25.375C22.8501 25.375 22.4583 24.9832 22.4583 24.5V22.1667C22.4583 21.1611 22.0588 20.1966 21.3478 19.4856C20.6367 18.7745 19.6723 18.375 18.6667 18.375H9.33332C8.32771 18.375 7.36329 18.7745 6.65221 19.4856C5.94113 20.1966 5.54166 21.1611 5.54166 22.1667V24.5C5.54166 24.9832 5.14991 25.375 4.66666 25.375C4.18341 25.375 3.79166 24.9832 3.79166 24.5V22.1667C3.79166 20.6969 4.37551 19.2874 5.41477 18.2481Z" fill="black" />
                </svg>
            ),
            closeIcon: (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" />
                </svg>
            ),
        },
    };

    const fallBackSignInIcon = headerMenuIcon.common.signInIcon;
    const closeIcon = headerMenuIcon.common.closeIcon;
    const fallBackUserIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M14 4.375C11.9059 4.375 10.2084 6.07259 10.2084 8.16667C10.2084 10.2607 11.9059 11.9583 14 11.9583C16.0941 11.9583 17.7917 10.2607 17.7917 8.16667C17.7917 6.07259 16.0941 4.375 14 4.375ZM8.45836 8.16667C8.45836 5.10609 10.9394 2.625 14 2.625C17.0606 2.625 19.5417 5.10609 19.5417 8.16667C19.5417 11.2272 17.0606 13.7083 14 13.7083C10.9394 13.7083 8.45836 11.2272 8.45836 8.16667ZM5.41477 18.2481C6.45404 17.2089 7.86358 16.625 9.33332 16.625H18.6667C20.1364 16.625 21.5459 17.2089 22.5852 18.2481C23.6245 19.2874 24.2083 20.6969 24.2083 22.1667V24.5C24.2083 24.9832 23.8166 25.375 23.3333 25.375C22.8501 25.375 22.4583 24.9832 22.4583 24.5V22.1667C22.4583 21.1611 22.0588 20.1966 21.3478 19.4856C20.6367 18.7745 19.6723 18.375 18.6667 18.375H9.33332C8.32771 18.375 7.36329 18.7745 6.65221 19.4856C5.94113 20.1966 5.54166 21.1611 5.54166 22.1667V24.5C5.54166 24.9832 5.14991 25.375 4.66666 25.375C4.18341 25.375 3.79166 24.9832 3.79166 24.5V22.1667C3.79166 20.6969 4.37551 19.2874 5.41477 18.2481Z" fill="black" />
        </svg>
    );

    const shortenFit = (s) => (s.length <= 6 ? s : `${s.substr(0, 6)}…`);
    const isMenuLinks = menu.filter((menuLink) => menuLink?.menu_links);

    let isMenuLinkInclude = isMenuLinks?.filter?.((items) => items?.menu_link?.filter?.((item) => item));

    if (isMenuLinkInclude?.length === 0) {
        isMenuLinkInclude = [];
        isMenuLinks?.forEach?.((item) => {
            if (item?.menu_links?.menu_link?.length > 1) {
                item?.menu_links?.menu_link?.forEach?.((links) => {
                    isMenuLinkInclude.push(links);
                });
            } else {
                isMenuLinkInclude.push(item?.menu_links?.menu_link?.[0]);
            }
        });
    }

    const SignInLink = isMenuLinkInclude.filter((item) => item?.link?.title?.includes('Login') || item?.link?.title?.includes('Sign In'));
    const myAccount = isMenuLinkInclude.filter((item) => item?.link?.title?.includes('My Account'));
    const getLogin = (
        <>
            {SignInLink.length > 0 && (
                <div className={classes.signInContainer}>
                    <div onClick={() => loginByRedirect()} className={classes.signInLink} role="presentation" id="loginButton">
                        {fallBackSignInIcon}
                        <span>Sign In</span>
                    </div>
                </div>
            )}
        </>
    );

    const getUserName = (
        <>
            {myAccount.length > 0 && (
                <div className={classes.signInContainer}>
                    <Link onClick={handleClickMenu} className={classes.signInLink} href={myAccount[0].link.href}>
                        {fallBackUserIcon}
                        <span>{shortenFit(getFullName?.firstName || '')}</span>
                    </Link>
                </div>
            )}
        </>
    );

    return (
        <div className={classes.headerContainer}>
            <div className={classes.header}>
                <div className={classes.headerLogo}>
                    <img src={logo?.image?.url} alt="logo" />
                </div>
                <div className={classes.closeSection}>
                    <div className={classes.headerIcons}>
                        {isAuthenticated ? (<>{getUserName}</>) : getLogin}
                    </div>
                    <span
                        role="presentation"
                        onClick={handleClickMenu}
                        className={classes.closeIcon}
                    >
                        {closeIcon}
                    </span>
                </div>
            </div>
        </div>
    );
};

MenuHeader.propTypes = {
    menu: array,
    handleClickMenu: func,
    logo: shape({
        image: shape({
            url: string,
        }),
    }),
    isAuthenticated: bool,
};

MenuHeader.defaultProps = {
    menu: [],
    handleClickMenu: () => {},
    logo: {},
    isAuthenticated: false,
};

export default MenuHeader;
